import React, { useState, useRef, useEffect } from 'react';
import './index.module.less';

import NavBar from '@components/NavBar';

import { Api } from '@apis/client';
import Http from '@helpers/Http';
import Filter from '@helpers/Filter';
import BrowserHistory from '@helpers/BrowserHistory';

import * as Cache from '../../../../../helpers/Cache';
import { ISelfClassDisableConfig, ISelfClassDisableTime } from '@apis/types';
import DateTimePicker from '@components/picker/EasyTimePicker';
import Toast from '@widgets/Toast';
import ClassDisableSelfRepeat from '../ClassDisableSelfRepeat';

const ClassDisableSelfEdit = () => {
  const params = useRef(Filter.urlParams(window.location.search));
  const [navBtnEnabled, setNavBtnEnabled] = useState(false);
  const [disableConfig, setDisableConfig] = useState<ISelfClassDisableConfig>({
    isDisableDuringClass: false,
    isDisableDuringHoliday: false,
    allowCallDuringDisableTime: false,
    allowSosDuringDisableTime: true,
    disableTimeConfigs: []
  });
  const [uid, setUid] = useState('');
  const [disableTime, setDisableTime] = useState<ISelfClassDisableTime>({name: '', isOpen: true, startTime: '', endTime: '', uid: '', dayIndexList: [0, 1, 2, 3, 4]});
  const [showRepeat, displayRepeat] = useState(false);

  const [showTimePicker, displayTimePicker] = useState({
    startTime: false,
    endTime: false,
  });

  const [time, setTime] = useState({
    startTime: '',
    endTime: '',
  });

  const title = params.current.action === 'add' ? '添加禁用时间' : '修改禁用时间';

  const textMap = {
    0: "周一",
    1: "周二",
    2: "周三",
    3: "周四",
    4: "周五",
    5: "周六",
    6: "周日",
  };

  let rightNavBtnStyle = {};
  if (!navBtnEnabled) {
    rightNavBtnStyle = {background: '#BED6FC'};
  }

  const getRepeatText = (): string => {
    const repeat = disableTime?.dayIndexList;
    if (repeat) {
      if (repeat.length === 7) {
        return '每天';
      }
      if (JSON.stringify([5, 6]) === JSON.stringify(repeat)) {
        return '周末';
      }
      if (JSON.stringify([0, 1, 2, 3, 4]) === JSON.stringify(repeat)) {
        return '工作日';
      }
      
      const daysTextArray = repeat.map((item) => textMap[item]);
      return daysTextArray.join("、");
    }
    return '';
  }

  const readNameText = (event: React.FocusEvent<HTMLInputElement>) => {
    setDisableTime({...disableTime, name: event.target.value});
  }

  const saveDisableConfig = async () => {
     const timeIndex = disableConfig.disableTimeConfigs.findIndex((config) => config.uid === uid);
     if (timeIndex >= 0) {
      disableConfig.disableTimeConfigs[timeIndex] = disableTime;
     } else {
      const newDisableTime = {...disableTime, uid: Date.now().toString()}
      setDisableTime(newDisableTime);
      disableConfig.disableTimeConfigs = [...disableConfig.disableTimeConfigs, newDisableTime];
     }
    await Http.post(Api.PostClassDisableSelfConfig, {
      sncode: params.current.sncode,
      company: params.current.company,
      ...disableConfig,
    }).then(() => {
      Toast.show({
        type: 'strip-bottom',
        title: '设置成功',
      });
      setTimeout(() => {
        BrowserHistory.goBack();
      }, 200);
    });
  }

  useEffect(() => {
    const item: ISelfClassDisableConfig = Cache.get('selfDisableConfig');
    const uid = Cache.get('disableUid');

    if (item) {
      setDisableConfig(item);
      if (uid && uid.length > 0) {
        const index = item.disableTimeConfigs.findIndex((config) => config.uid === uid);
        if (index >= 0) {
          setDisableTime(item.disableTimeConfigs[index]);
          setTime({startTime: item.disableTimeConfigs[index].startTime, endTime: item.disableTimeConfigs[index].endTime});
        }
      }
    }
    setUid(uid || '');
  }, []);

  const translateTimestamp = (time: string)=> {
    let s = 0;
    if (time !== null) {
      const hour = time.split(":")[0];
      const min = time.split(":")[1];
      s = Number(hour) * 3600 + Number(min) * 60;
    }
    return s;
  }

  const verifyTime = (value: string, type: string): boolean => {
    if (type === 'startTime' && time.endTime) {
      const start = translateTimestamp(value);
      const end = translateTimestamp(time.endTime);
      if (start >= end) {
        Toast.show({
          type: 'strip',
          title: '开始时间不得晚于结束时间'
        });
        return false;
      }
    } else if (type === 'endTime' && time.startTime) {
      const start = translateTimestamp(time.startTime);
      const end = translateTimestamp(value);
      if (end <= start) {
        Toast.show({
          type: 'strip',
          title: '结束时间不得早于开始时间'
          });
        return false;
      }
    }
    return true;
  }

  const confirmTime = (value: string, type: string) => {
    if (verifyTime(value, type)) {
      setTime({
        ...time,
        [type]: value,
      });
      setDisableTime({...disableTime, [type]: value});
    }
  };

  const controlTimePicker = (data = {}) => {
    displayTimePicker({
      ...showTimePicker,
      ...data,
    });
  };

  useEffect(() => {
    if (disableTime?.name.trim() && disableTime.startTime && disableTime.endTime && disableTime.dayIndexList.length > 0) {
      setNavBtnEnabled(true);
    } else {
      setNavBtnEnabled(false);
    }
  }, [disableTime]);

  return (
    <div styleName="container">
      <NavBar
        title={title}
        RightBtnComponent={
          <div styleName="nav-right-btn" style={rightNavBtnStyle} onClick={navBtnEnabled ? saveDisableConfig : null}>保存</div>
        } />
        <div styleName="list">
          <div className="flex" styleName='item-container top-border bottom-border'>
            <div className="flex" styleName='item-content'>
              <span styleName='item-title'>名称</span>
              <input styleName='input-text' placeholder='请输入名称' maxLength={10} defaultValue={disableTime?.name || ''} onBlur={readNameText} onChange={readNameText}/>
            </div>
          </div> 

          <div className="flex" styleName='item-container top-border top-margin' onClick={() =>controlTimePicker({ startTime: true })}>
            <div className="flex" styleName='item-content'>
              <span styleName='item-title'>开始时间</span>
              <div className="flex" styleName='right-container'>
                <span styleName="item-subtext">{disableTime?.startTime || '请选择'} </span>
                <i styleName='right-arrow' className="iconfont icon-tubiaoyanse" />
              </div>
            </div>
            <div styleName='item-sep'/>
          </div>

          <div className="flex" styleName='item-container bottom-border' onClick={() =>controlTimePicker({ endTime: true })}>
            <div className="flex" styleName='item-content'>
              <span styleName='item-title'>结束时间</span>
              <div className="flex" styleName='right-container'>
                <span styleName="item-subtext">{disableTime?.endTime || '请选择'} </span>
                <i styleName='right-arrow' className="iconfont icon-tubiaoyanse" />
              </div>
            </div>
          </div>

          <div className="flex" styleName='item-container top-border bottom-border top-margin' onClick={() => displayRepeat(true)}>
            <div className="flex" styleName='item-content'>
              <span styleName='item-title'>重复</span>
              <div className="flex" styleName='right-container'>
                <span styleName="item-subtext">{getRepeatText()} </span>
                <i styleName='right-arrow' className="iconfont icon-tubiaoyanse" />
              </div>
            </div>
          </div>
        </div>
        {showTimePicker.startTime ? (
          <DateTimePicker
            title="选择时间"
            value={time.startTime}
            onConfirm={(value: string) => confirmTime(value, 'startTime')}
            onClose={() => controlTimePicker({ startTime: false })}
          />
        ) : null}
        {showTimePicker.endTime ? (
          <DateTimePicker
          title="选择时间"
            value={time.endTime}
            onConfirm={(value: string) => confirmTime(value, 'endTime')}
            onClose={() => controlTimePicker({ endTime: false })}
          />
        ) : null}
        {showRepeat ? (
          <ClassDisableSelfRepeat
            repeatData={disableTime.dayIndexList}
            onClose={() => displayRepeat(false)}
            onConfirm={(data) => setDisableTime({...disableTime, dayIndexList: data})}
          />
        ) : null}
    </div>
  )
}

export default ClassDisableSelfEdit;
